import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, PartyPopper } from 'lucide-react';
import { HomeNavbar } from '../components/home/HomeNavbar';

export const DemoPage = () => {
  const [form, setForm] = useState({
    fullName: '',
    workEmail: '',
    phone: '',
    company: '',
    profile: '',          // "brand" | "agency"
    currentSetup: '',     // "in_house" | "external" | "both"
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    // No real submission — front-end thank-you state only per spec.
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-[#0F0F0F] scroll-smooth" data-testid="demo-page">
      <HomeNavbar />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-73px)]">
        {/* ─── LEFT — dark rail with value prop + quote ─── */}
        <aside className="bg-[#0F0F0F] text-white px-6 md:px-12 lg:px-16 py-14 md:py-20 relative overflow-hidden" data-testid="demo-left-rail">
          <div
            className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.14] pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, #FF451A, transparent)' }}
          />
          <div className="relative max-w-lg mx-auto lg:mx-0">
            <div className="text-xl font-bold tracking-tight mb-10">
              <span className="text-white">Vy</span>
              <span className="text-[#FF451A]">vre</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="demo-heading">
              Talk to Our Team
            </h1>
            <p className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed">
              See how Vyvre can help you run influencer campaigns without the chaos — in a 15 minute call.
            </p>

            <ul className="mt-8 space-y-3" data-testid="demo-benefits">
              {[
                'Find the right creators from 19,000+ vetted profiles',
                'Launch campaigns in days, not weeks',
                'Give your brand team real-time visibility',
                'Get campaign reports that actually prove ROI',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm md:text-[15px] text-zinc-200">
                  <CheckCircle2 size={18} className="text-[#FF451A] shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            {/* Quote card */}
            <div
              className="mt-12 border border-white/10 rounded-2xl p-5 md:p-6 bg-white/[0.03] backdrop-blur"
              data-testid="demo-quote-card"
            >
              <blockquote className="text-sm md:text-[15px] text-zinc-200 leading-relaxed italic">
                &ldquo;StrawHat&apos;s platform gave us complete visibility into our campaign — every creator, every
                deliverable, every invoice. No more chasing updates.&rdquo;
              </blockquote>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF451A] flex items-center justify-center text-white font-bold shrink-0">
                  Z
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Zaid Thakur</div>
                  <div className="text-xs text-zinc-400">Head of Operations, StrawHat Network</div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* ─── RIGHT — form (or thank-you) ─── */}
        <section className="bg-white px-6 md:px-12 lg:px-16 py-14 md:py-20 flex items-start" data-testid="demo-right-form">
          <div className="w-full max-w-md mx-auto lg:mx-0">
            {submitted ? (
              <ThankYou onReset={() => { setSubmitted(false); setForm({ fullName: '', workEmail: '', phone: '', company: '', profile: '', currentSetup: '' }); }} />
            ) : (
              <>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0F0F0F]">Schedule a Demo</h2>
                <p className="text-sm text-[#6B7280] mt-2 mb-8">
                  Fill in the details below and our team will reach out within 24 hours.
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="demo-form">
                  <TextField
                    label="Full Name" required
                    value={form.fullName}
                    onChange={(v) => update('fullName', v)}
                    placeholder="Priya Sharma"
                    testid="demo-field-name"
                  />
                  <TextField
                    label="Work Email" required type="email"
                    value={form.workEmail}
                    onChange={(v) => update('workEmail', v)}
                    placeholder="priya@company.com"
                    testid="demo-field-email"
                  />
                  <TextField
                    label="Phone Number" required type="tel"
                    value={form.phone}
                    onChange={(v) => update('phone', v)}
                    placeholder="+91 98765 43210"
                    testid="demo-field-phone"
                  />
                  <TextField
                    label="Company Name" required
                    value={form.company}
                    onChange={(v) => update('company', v)}
                    placeholder="Acme Beauty"
                    testid="demo-field-company"
                  />
                  <RadioGroup
                    label="What best describes you?" required
                    value={form.profile}
                    onChange={(v) => update('profile', v)}
                    options={[
                      { value: 'brand',  label: 'Brand' },
                      { value: 'agency', label: 'Agency' },
                    ]}
                    testid="demo-field-profile"
                  />
                  <RadioGroup
                    label="How do you currently run influencer campaigns?" required
                    value={form.currentSetup}
                    onChange={(v) => update('currentSetup', v)}
                    options={[
                      { value: 'in_house', label: 'In-House Team' },
                      { value: 'external', label: 'External Agency' },
                      { value: 'both',     label: 'Both' },
                    ]}
                    testid="demo-field-setup"
                  />
                  <button
                    type="submit"
                    className="w-full mt-2 bg-[#FF451A] hover:bg-[#E63E17] text-white rounded-full px-6 py-3 font-semibold text-sm inline-flex items-center justify-center gap-2 shadow-[0_6px_24px_rgba(255,69,26,0.28)] transition-colors"
                    data-testid="demo-submit-button"
                  >
                    Book My Demo <ArrowRight size={14} />
                  </button>
                  <p className="text-[11px] text-[#6B7280] text-center pt-1">
                    By submitting, you agree to be contacted by our team about Vyvre.
                  </p>
                </form>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

// ───────────────────────── Sub-components ─────────────────────────

const TextField = ({ label, value, onChange, required, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-[#4B5563] mb-1.5">
      {label} {required && <span className="text-[#FF451A]">*</span>}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 rounded-xl border border-black/[0.10] bg-white text-sm text-[#0F0F0F] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#FF451A] focus:ring-2 focus:ring-[#FF451A]/15 transition-all"
      data-testid={testid}
    />
  </div>
);

const RadioGroup = ({ label, value, onChange, options, required, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-[#4B5563] mb-1.5">
      {label} {required && <span className="text-[#FF451A]">*</span>}
    </label>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              active
                ? 'bg-[#FF451A] text-white border-[#FF451A] shadow-[0_4px_14px_rgba(255,69,26,0.24)]'
                : 'bg-white text-[#0F0F0F] border-black/[0.12] hover:border-[#FF451A]/50'
            }`}
            data-testid={`${testid}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
    {required && !value && (
      // Hidden required-mimic: without a hidden input, the radio-as-buttons wouldn't
      // trigger native form validation. Keep it visually hidden but focusable.
      <input tabIndex={-1} required value={value} onChange={() => {}} className="sr-only" aria-hidden="true" />
    )}
  </div>
);

const ThankYou = ({ onReset }) => (
  <div className="text-center py-6" data-testid="demo-thank-you">
    <div className="w-14 h-14 rounded-full bg-[#FF451A]/10 flex items-center justify-center mx-auto mb-5">
      <PartyPopper size={26} className="text-[#FF451A]" />
    </div>
    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0F0F0F]">Thanks!</h2>
    <p className="mt-3 text-sm md:text-base text-[#4B5563] max-w-sm mx-auto leading-relaxed">
      We&apos;ll reach out within 24 hours to schedule your 15 minute demo.
    </p>
    <button
      type="button"
      onClick={onReset}
      className="mt-8 text-sm font-semibold text-[#FF451A] hover:underline"
      data-testid="demo-submit-again"
    >
      Submit another request
    </button>
  </div>
);

export default DemoPage;
